import type { ShadowStop } from '../../tokens/Foundation/Foundation';

export type ShadowCascade = Readonly<Record<ShadowStop, string>>;

export interface BuildOptions {
  mode: 'light' | 'dark';
}

interface IShadowCascadeBuilder {
  build(options: BuildOptions): ShadowCascade;
  buildInset(options: BuildOptions): string;
}

interface CascadeRow {
  blur: string;
  offsetY: string;
  spread: string;
  stop: ShadowStop;
}

/**
 * 5-stop foundation shadow cascade per design Appendix A.
 * Tuned to match the default shadow scale; alpha values are dialed
 * based on `mode` (dark-mode shadows are weaker / more diffuse per principles §3).
 */
const CASCADE: readonly CascadeRow[] = [
  { blur: '2px', offsetY: '1px', spread: '0', stop: 'xs' },
  { blur: '3px', offsetY: '1px', spread: '0', stop: 'sm' },
  { blur: '6px', offsetY: '4px', spread: '-1px', stop: 'md' },
  { blur: '15px', offsetY: '10px', spread: '-3px', stop: 'lg' },
  { blur: '25px', offsetY: '20px', spread: '-5px', stop: 'xl' }
];

export const ShadowCascadeBuilder: IShadowCascadeBuilder = {
  /**
   * Build the 5-stop shadow cascade for a given mode.
   * @param options Mode selector.
   * @returns Shadow tokens keyed by stop with full `box-shadow` strings.
   */
  build(options: BuildOptions): ShadowCascade {
    const alpha = options.mode === 'dark' ? 0.4 : 0.1;
    const result: Partial<Record<ShadowStop, string>> = {};
    for (const row of CASCADE) {
      result[row.stop] = `0 ${row.offsetY} ${row.blur} ${row.spread} oklch(0 0 0 / ${alpha})`;
    }
    return result as ShadowCascade;
  },
  /**
   * Build the inset shadow string for a given mode. Light alpha (`0.06`)
   * preserves the pre-Phase-6 literal; dark alpha (`0.4`) matches the regular
   * cascade so the inset reads against the lifted dark canvas where a `0.06`
   * inset would disappear.
   * @param options Mode selector.
   * @returns CSS inset `box-shadow` string.
   */
  buildInset(options: BuildOptions): string {
    const alpha = options.mode === 'dark' ? 0.4 : 0.06;
    return `inset 0 1px 2px 0 oklch(0 0 0 / ${alpha})`;
  }
};
